import { SQL_WORKER_SOURCE } from "./sqlWorkerSource";

export const SQL_RUN_TIMEOUT_MS = 20_000;

export type SqlCell = string | number | null;

export type SqlTableResult = {
  columns?: string[];
  rows?: SqlCell[][];
  /** Сколько строк вернул запрос до обрезки. */
  total?: number;
  truncated?: boolean;
  note?: string;
  error?: string;
};

export type SqlForeignKey = {
  /** Столбец-ссылка в этой таблице. */
  from: string;
  /** Таблица, на которую он ссылается. */
  table: string;
  /** Столбец в родительской таблице; null — первичный ключ по умолчанию. */
  to: string | null;
};

export type SqlSchemaTable = {
  name: string;
  columns: { name: string; type: string; pk: boolean }[];
  rows: number;
  fks?: SqlForeignKey[];
};

export type SqlRunRequest = {
  setup: string;
  query: string;
  /** Если задан — выполняется вторым и возвращается как эталон. */
  solution?: string;
};

export type SqlRunResponse = {
  actual: SqlTableResult;
  expected?: SqlTableResult;
  schema?: SqlSchemaTable[];
};

let worker: Worker | null = null;
let workerUrl: string | null = null;
let nextReqId = 1;

function ensureWorker(): Worker {
  if (worker) return worker;
  const blob = new Blob([SQL_WORKER_SOURCE], { type: "application/javascript" });
  workerUrl = URL.createObjectURL(blob);
  worker = new Worker(workerUrl);
  return worker;
}

function killWorker(): void {
  if (worker) {
    try {
      worker.terminate();
    } catch {
      /* ignore */
    }
    worker = null;
  }
  if (workerUrl) {
    URL.revokeObjectURL(workerUrl);
    workerUrl = null;
  }
}

/**
 * Выполняет запрос (и, если нужно, эталон) в отдельном Worker-е.
 * Тяжёлый запрос не вешает доску: по таймауту Worker убивается.
 */
export function runSql(
  req: SqlRunRequest,
  onPhase?: (phase: "loading_runtime" | "running") => void
): Promise<SqlRunResponse> {
  const w = ensureWorker();
  const reqId = nextReqId++;

  return new Promise<SqlRunResponse>((resolve) => {
    let settled = false;
    let timer = 0;

    const finish = (res: SqlRunResponse) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      w.removeEventListener("message", onMessage);
      w.removeEventListener("error", onError);
      resolve(res);
    };

    const onMessage = (ev: MessageEvent) => {
      const msg = ev.data || {};
      if (msg.reqId !== reqId) return;
      if (msg.type === "phase") {
        onPhase?.(msg.phase);
        return;
      }
      if (msg.type === "result") {
        finish({
          actual: (msg.actual as SqlTableResult) ?? { error: "Пустой ответ" },
          expected: msg.expected as SqlTableResult | undefined,
          schema: msg.schema as SqlSchemaTable[] | undefined,
        });
      }
    };

    const onError = (err: ErrorEvent) => {
      killWorker();
      finish({ actual: { error: err.message || "Ошибка SQL-движка" } });
    };

    w.addEventListener("message", onMessage);
    w.addEventListener("error", onError);

    timer = window.setTimeout(() => {
      killWorker();
      finish({
        actual: {
          error: `Запрос выполнялся дольше ${Math.round(
            SQL_RUN_TIMEOUT_MS / 1000
          )} с и был остановлен. Скорее всего, не хватает условия соединения таблиц.`,
        },
      });
    }, SQL_RUN_TIMEOUT_MS);

    try {
      w.postMessage({ type: "run", reqId, setup: req.setup, query: req.query, solution: req.solution });
    } catch (e) {
      killWorker();
      finish({ actual: { error: e instanceof Error ? e.message : String(e) } });
    }
  });
}

/* ────────────────────────── сравнение с эталоном ────────────────────────── */

export type SqlVerdict = {
  ok: boolean;
  /** Короткая формулировка для ученицы. */
  message: string;
};

function normCell(v: SqlCell): string {
  if (v === null || v === undefined) return " NULL";
  if (typeof v === "number") {
    if (!Number.isFinite(v)) return String(v);
    // 1200 и 1200.0 — один и тот же ответ; дробные округляем до 6 знаков.
    return String(Math.round(v * 1e6) / 1e6);
  }
  const s = String(v).trim();
  const asNum = Number(s.replace(",", "."));
  if (s !== "" && Number.isFinite(asNum)) return String(Math.round(asNum * 1e6) / 1e6);
  return s.replace(/\s+/g, " ").toLowerCase();
}

function rowKey(row: SqlCell[]): string {
  return row.map(normCell).join("");
}

function plural(n: number, one: string, few: string, many: string): string {
  const n10 = n % 10;
  const n100 = n % 100;
  if (n10 === 1 && n100 !== 11) return one;
  if (n10 >= 2 && n10 <= 4 && (n100 < 12 || n100 > 14)) return few;
  return many;
}

function rowsWord(n: number): string {
  return `${n} ${plural(n, "строка", "строки", "строк")}`;
}

function colsWord(n: number): string {
  return `${n} ${plural(n, "столбец", "столбца", "столбцов")}`;
}

/** Порядок важен только если эталон явно сортирует результат. */
export function solutionOrdersRows(solution: string): boolean {
  return /\border\s+by\b/i.test(solution);
}

/**
 * Сравнивает результат ученика с эталоном.
 * Имена столбцов не проверяются (псевдонимы у всех разные), проверяются данные.
 */
export function compareResults(
  actual: SqlTableResult,
  expected: SqlTableResult,
  orderMatters: boolean
): SqlVerdict {
  if (actual.error) {
    return { ok: false, message: "Запрос не выполнился — исправьте ошибку и попробуйте снова." };
  }
  if (expected.error) {
    return { ok: false, message: "Не удалось выполнить эталонный запрос задания: " + expected.error };
  }
  const aRows = actual.rows ?? [];
  const eRows = expected.rows ?? [];
  const aCols = actual.columns?.length ?? 0;
  const eCols = expected.columns?.length ?? 0;

  if (actual.truncated || expected.truncated) {
    return {
      ok: false,
      message: "Слишком много строк для проверки — уточните условие в запросе.",
    };
  }

  if (aCols < eCols) {
    return {
      ok: false,
      message: `Неверно: не хватает столбцов. Нужно ${colsWord(eCols)}, а получилось ${aCols}.`,
    };
  }

  if (aRows.length !== eRows.length) {
    return {
      ok: false,
      message: `Неверно: получилось ${rowsWord(aRows.length)}, а должно быть ${rowsWord(
        eRows.length
      )}. Проверьте условие в WHERE или соединение таблиц.`,
    };
  }

  if (aCols === eCols) return compareRows(aRows, eRows, orderMatters);

  // Столбцов больше, чем в эталоне.
  //
  // Это не ошибка: чтобы увидеть своими глазами, почему строка попала в ответ,
  // к фамилии дописывают столбец с NULL — и раньше такой ответ не
  // засчитывался. Поэтому ищем среди столбцов ответа те, что дают эталон, и
  // проверяем уже их. Нет таких — значит нужных данных в ответе правда нет.
  const picked = pickExpectedColumns(aRows, eRows, aCols, eCols, orderMatters);
  if (!picked) {
    // Нужные столбцы могут быть на месте, а порядок строк — нет: про это
    // полезнее сказать прямо, чем жаловаться на столбцы.
    if (orderMatters && pickExpectedColumns(aRows, eRows, aCols, eCols, false)) {
      return {
        ok: false,
        message: "Почти! Строки те же, но идут в другом порядке — нужен ORDER BY.",
      };
    }
    return {
      ok: false,
      message: "Неверно: среди выведенных столбцов нет тех, что нужны по условию.",
    };
  }
  return { ok: true, message: "Верно! Лишние столбцы не мешают — данные те, что нужны." };
}

/** Сверка строк: по порядку, если эталон сортирует, иначе как наборов. */
function compareRows(
  aRows: SqlCell[][],
  eRows: SqlCell[][],
  orderMatters: boolean
): SqlVerdict {
  const aKeys = aRows.map(rowKey);
  const eKeys = eRows.map(rowKey);

  if (orderMatters) {
    for (let i = 0; i < aKeys.length; i++) {
      if (aKeys[i] !== eKeys[i]) {
        const sameSet = [...aKeys].sort().join("|") === [...eKeys].sort().join("|");
        return {
          ok: false,
          message: sameSet
            ? "Почти! Строки те же, но идут в другом порядке — нужен ORDER BY."
            : `Неверно: строка ${i + 1} отличается от ожидаемой.`,
        };
      }
    }
    return { ok: true, message: "Верно!" };
  }

  const bag = new Map<string, number>();
  for (const k of eKeys) bag.set(k, (bag.get(k) ?? 0) + 1);
  for (const k of aKeys) {
    const left = bag.get(k);
    if (!left) {
      return {
        ok: false,
        message: "Неверно: строк столько же, сколько нужно, но данные в них другие.",
      };
    }
    bag.set(k, left - 1);
  }
  return { ok: true, message: "Верно!" };
}

/**
 * Ищет в ответе столбцы, которые дают эталон.
 *
 * Перебор узкий: столбец годится в кандидаты, только если его значения как
 * набор совпадают с нужным столбцом эталона, а дальше проверяется уже целая
 * строка — случайное совпадение по одному столбцу ничего не засчитает.
 */
function pickExpectedColumns(
  aRows: SqlCell[][],
  eRows: SqlCell[][],
  aCols: number,
  eCols: number,
  orderMatters: boolean
): number[] | null {
  const columnBag = (rows: SqlCell[][], index: number): string =>
    rows
      .map((r) => normCell(r[index]))
      .sort()
      .join("\u0001");

  const expectedBags = Array.from({ length: eCols }, (_, j) => columnBag(eRows, j));
  const actualBags = Array.from({ length: aCols }, (_, i) => columnBag(aRows, i));

  const candidates = expectedBags.map((bag) =>
    actualBags.map((b, i) => (b === bag ? i : -1)).filter((i) => i >= 0)
  );
  if (candidates.some((list) => list.length === 0)) return null;

  const chosen: number[] = [];
  const used = new Set<number>();

  const walk = (j: number): boolean => {
    if (j === eCols) {
      const projected = aRows.map((row) => chosen.map((i) => row[i] as SqlCell));
      return compareRows(projected, eRows, orderMatters).ok;
    }
    for (const index of candidates[j]!) {
      if (used.has(index)) continue;
      used.add(index);
      chosen.push(index);
      if (walk(j + 1)) return true;
      chosen.pop();
      used.delete(index);
    }
    return false;
  };

  return walk(0) ? chosen : null;
}
